import React from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { ChangeUserPassword } from '../../store/UserSlice';
import { AppDispatch } from '../../store/store';
import { changePasswordSchema, ChangePasswordFormData } from '../../schemas/auth.schema';
import Input from '../form/Input';

export default function ChangePassword() {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      password: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    try {
      const result = await dispatch(ChangeUserPassword(data));
      if (ChangeUserPassword.fulfilled.match(result)) {
        toast.success(t('profile.passwordChanged'));
        reset();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error?.message || t('profile.passwordChangeFailed'));
    }
  };

  return (
    <div className="w-full lg:w-1/2 p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="max-w-2xl m-auto">
          <div className="pt-4 w-full relative">
            <Input
              id="password"
              label={t('profile.currentPasswordLabel')}
              type="password"
              placeholder={t('profile.currentPasswordPlaceholder')}
              showPasswordToggle={true}
              error={errors.password?.message}
              {...register('password')}
            />
          </div>

          <div className="pt-4 lg:pt-8 w-full relative">
            <Input
              id="newPassword"
              label={t('profile.newPasswordLabel')}
              type="password"
              placeholder={t('profile.newPasswordPlaceholder')}
              showPasswordToggle={true}
              error={errors.newPassword?.message}
              {...register('newPassword')}
            />
          </div>

          <div className="pt-4 lg:pt-8 w-full relative">
            <Input
              id="confirmPassword"
              label={t('profile.confirmPasswordLabel')}
              type="password"
              placeholder={t('profile.confirmPasswordPlaceholder')}
              showPasswordToggle={true}
              error={errors.confirmPassword?.message}
              {...register('confirmPassword')}
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#037897] text-white font-semibold float-left w-auto min-w-[200px] px-8 py-3 whitespace-nowrap mt-8 mb-8 rounded-md hover:bg-[#025f77] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('common.loading') : t('profile.changePasswordBtn')}
          </button>
        </div>
      </form>
    </div>
  );
}
